import React, { useState, useMemo } from 'react';
import ActionButton from '../components/ActionButton';
import DishCard from '../components/DishCard';
import ComboCard from '../components/ComboCard';
import DataStatusBanner from '../components/DataStatusBanner';
import useRemoteData from '../hooks/useRemoteData';
import { fetchAddOns, fetchCombos, fetchMenuItems } from '../lib/api';

const MenuPage = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: menu, loading: menuLoading, error: menuError } = useRemoteData(fetchMenuItems, [], { cacheKey: 'menu-items' });
  const { data: combos, loading: combosLoading, error: combosError } = useRemoteData(fetchCombos, [], { cacheKey: 'combos' });
  const { data: addOns, loading: addOnsLoading, error: addOnsError } = useRemoteData(fetchAddOns, [], { cacheKey: 'dish-add-ons' });

  // Search logic
  const filteredMenu = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return normalizedQuery
      ? menu.filter((item) => {
          const name = item.name?.toLowerCase() || '';
          const description = item.description?.toLowerCase() || '';
          const category = item.category?.toLowerCase() || '';
          const tags = Array.isArray(item.tags)
            ? item.tags.join(' ').toLowerCase()
            : '';

          return (
            name.includes(normalizedQuery) ||
            description.includes(normalizedQuery) ||
            category.includes(normalizedQuery) ||
            tags.includes(normalizedQuery)
          );
        })
      : [...menu];
  }, [menu, searchQuery]);

  return (
    <main className="menu-page">
      {/* Hero Section */}
      <section className="menu-hero">
        <div className="menu-hero-content">
          <p className="eyebrow">Browse our menu</p>
          <p>Handcrafted daily. Customizable portions.</p>
        </div>
      </section>

      <section className="menu-controls">
        <div className="search-wrapper">
          <span className="search-icon" aria-hidden="true">🔎</span>
          <input
            type="search"
            className="menu-search-input"
            placeholder="Search dishes by name, category, or tags"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            aria-label="Search dishes"
          />
        </div>

        <div className="filter-controls">
          <p className="results-count">
            {filteredMenu.length} dish{filteredMenu.length === 1 ? '' : 'es'} found
          </p>
        </div>
      </section>

      {/* Menu Grid */}
      <section className="menu-grid-section">
        <DataStatusBanner
          loading={menuLoading || addOnsLoading}
          error={menuError || addOnsError}
          empty={!menuLoading && !menuError && menu.length === 0}
          emptyMessage="Menu items are unavailable right now. Please check back shortly."
        />
        {!menuLoading && !menuError && menu.length > 0 && (
          filteredMenu.length > 0 ? (
            <div className="menu-grid">
              {filteredMenu.map((dish) => (
                <DishCard key={dish.id} dish={dish} addOnOptions={addOns} />
              ))}
            </div>
          ) : (
            <div className="no-results">
              <div className="no-results-icon">🍽️</div>
              <h3>No dishes found</h3>
              <p>Try a different search keyword</p>
              <ActionButton
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                }}
              >
                Clear search
              </ActionButton>
            </div>
          )
        )}
      </section>

      {/* Combos Section */}
      <section className="menu-combos">
        <div className="combos-preview">
          <ActionButton to="/combos" variant="outline" size="sm">View more combo packages</ActionButton>
          <DataStatusBanner
            loading={combosLoading}
            error={combosError}
            empty={!combosLoading && !combosError && combos.length === 0}
            emptyMessage="Combo packages are unavailable right now."
          />
          {!combosLoading && !combosError && combos.length > 0 && (
            <div className="combos-grid">
              {combos.slice(0, 3).map((combo) => (
                <ComboCard key={combo.id} combo={combo} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="menu-info">
        <div className="info-card">
          <div className="info-icon">🥘</div>
          <h3>Fully customizable</h3>
          <p>Adjust spice levels, swap ingredients, or request portion sizes that work for you.</p>
        </div>
        <div className="info-card">
          <div className="info-icon">🚚</div>
          <h3>Fast delivery</h3>
          <p>Order online or call. Pickup from our shop or delivery across Asaba, Delta State same-day.</p>
        </div>
        <div className="info-card">
          <div className="info-icon">👥</div>
          <h3>Event orders</h3>
          <p>Need meals for 50+ people? Check out our catering services and bulk ordering options.</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="menu-cta">
        <h2>Ready to order?</h2>
        <p>Place your order now for quick pickup or delivery to your location.</p>
        <div className="menu-cta-buttons">
          <ActionButton to="/cart" variant="primary" size="sm">
            Place an order
          </ActionButton>
        </div>
      </section>
    </main>
  );
};

export default MenuPage;
